import { createContext, useContext, useReducer, useEffect } from 'react'
import { authAPI } from '../services/api'
import toast from 'react-hot-toast'

const AuthContext = createContext()

const initialState = {
  user: null,
  loading: true,
  error: null
}

const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_USER':
      return { ...state, user: action.payload, loading: false }
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }
    case 'LOGOUT':
      return { user: null, loading: false, error: null }
    default:
      return state
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  useEffect(() => {
    const initAuth = async () => {
      try {
        const response = await authAPI.verify()
        dispatch({ type: 'SET_USER', payload: response.data.user })
      } catch (error) {
        dispatch({ type: 'LOGOUT' })
      }
    }

    initAuth()
  }, [])

  const login = async (code) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const response = await authAPI.googleCallback(code)

      dispatch({ type: 'SET_USER', payload: response.data.user })
      
      toast.success(`Welcome, ${response.data.user.name}!`)
      return true
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
      toast.error(error.response?.data?.message || 'Login failed')
      return false
    }
  }

  const logout = async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      dispatch({ type: 'LOGOUT' })
      toast.success('Logged out successfully')
    }
  }

  const getGoogleAuthUrl = async () => {
    try {
      const response = await authAPI.getGoogleAuthUrl()
      return response.data.url
    } catch (error) {
      toast.error('Failed to get Google auth URL')
      throw error
    }
  }

  const value = {
    ...state,
    login,
    logout,
    getGoogleAuthUrl
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
